import { useState } from "react";

import {
  SeletorEscopo,
  type EscopoSelecionado,
} from "../projecao/SeletorEscopo";
import { ControleHorizonte } from "../projecao/ControleHorizonte";
import { SaldoCards } from "../dashboard/SaldoCards";
import { AlertasBloco } from "../dashboard/AlertasBloco";
import { FilaPendentes } from "../dashboard/FilaPendentes";
import { DiasDeRiscoTabela } from "../dashboard/DiasDeRiscoTabela";
import { useDashboard } from "../../lib/dashboard/hooks";
import { ErroHttp } from "../../lib/http/erros";
import { BotaoExportarExtrato } from "../export/BotaoExportarExtrato";
import { useTemPermissao } from "../../lib/auth/permissoes";

/*
 * Página Dashboard (Tela 1 §10.5) — landing em `/` (substitui o placeholder
 * Painel). Reusa o `SeletorEscopo` da projeção (empresa | grupo, grupo só para
 * irrestrito) e o `ControleHorizonte` com TETO 120 (`maximo={120}`): o backend
 * do dashboard rejeita horizonte >120 (422), então o FE clampa antes de
 * disparar — sem 422 inútil. Default 30 do contrato.
 *
 * ESCOPO: `escopo: EscopoSelecionado | null` — empresa (id) ou grupo
 * (consolidado). `escopo === null` (nada escolhido) → mensagem de seleção e
 * NENHUM fetch. Com escopo escolhido, chama `useDashboard(empresaId, horizonte,
 * habilitado=true)`, onde `empresaId` é `null` no ramo grupo (omite `empresa_id`
 * no wire). O `habilitado` distingue "grupo escolhido (null, dispara)" de "nada
 * escolhido (não dispara)".
 *
 * O backend gateia `VISUALIZAR_PROJECAO` (todos os perfis têm) e o `SeletorEscopo`
 * já restringe a opção grupo a irrestrito — sem gate de UI próprio.
 *
 * ESTADOS de erro (sinais tipados do `ErroHttp`): 503 → módulo indisponível;
 * demais `ErroHttp` → mensagem do erro; não-`ErroHttp` → fallback genérico.
 */

/** Horizonte padrão do dashboard (espelha o default do backend). */
const HORIZONTE_PADRAO = 30;

/** Ação RBAC do export de extrato — string EXATA do StrEnum do backend. */
const ACAO_EXPORTAR_EXTRATO = "exportar_extrato";

/**
 * Teto do horizonte no dashboard. O backend do dashboard rejeita horizonte >120
 * (422); o `ControleHorizonte` clampa neste teto, evitando disparar uma query
 * que o backend recusaria com mensagem genérica.
 */
const HORIZONTE_MAXIMO = 120;

export function Dashboard(): JSX.Element {
  const [escopo, setEscopo] = useState<EscopoSelecionado | null>(null);
  const [horizonte, setHorizonte] = useState<number>(HORIZONTE_PADRAO);
  const podeExportar = useTemPermissao(ACAO_EXPORTAR_EXTRATO);

  // Empresa selecionada (id) ou `null` na visão grupo — o export é EMPRESA-only.
  const empresaIdExport =
    escopo?.tipo === "empresa" ? escopo.empresaId : null;

  return (
    <section className="flex flex-col gap-6">
      <h1 className="font-titulo text-2xl text-off-black">Dashboard</h1>

      <div className="flex flex-wrap items-end gap-6">
        <SeletorEscopo selecionado={escopo} aoSelecionar={setEscopo} />
        <ControleHorizonte
          valor={horizonte}
          aoMudar={setHorizonte}
          minimo={1}
          maximo={HORIZONTE_MAXIMO}
        />
        <BotaoExportarExtrato
          empresaId={empresaIdExport}
          horizonte={horizonte}
          podeExportar={podeExportar}
        />
      </div>

      {escopo === null ? (
        <p className="font-corpo text-base text-off-black">
          Selecione um escopo para ver o dashboard.
        </p>
      ) : (
        <VisaoDashboard
          empresaId={escopo.tipo === "empresa" ? escopo.empresaId : null}
          horizonte={horizonte}
        />
      )}
    </section>
  );
}

interface VisaoDashboardProps {
  /** Empresa selecionada, ou `null` para a visão de GRUPO (consolidado). */
  empresaId: string | null;
  horizonte: number;
}

/**
 * Visão do dashboard para um escopo já escolhido (empresa ou grupo). Dispara o
 * `useDashboard` com `habilitado=true` — só é montada quando há escopo, então o
 * fetch é sempre desejado. Renderiza os quatro blocos da Tela 1.
 */
function VisaoDashboard({
  empresaId,
  horizonte,
}: VisaoDashboardProps): JSX.Element {
  const consulta = useDashboard(empresaId, horizonte, true);

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando dashboard…
      </p>
    );
  }

  if (consulta.isError) {
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagemErro(consulta.error)}
      </p>
    );
  }

  const dashboard = consulta.data;

  return (
    <div className="flex flex-col gap-6">
      <SaldoCards saldo={dashboard.saldo} horizonte={horizonte} />
      <div className="grid gap-6 md:grid-cols-2">
        <AlertasBloco alertas={dashboard.alertas} />
        <FilaPendentes fila={dashboard.fila_conciliacao} />
      </div>
      <section className="flex flex-col gap-2">
        <h2 className="font-subtitulo text-base text-off-black">
          Dias de risco
        </h2>
        <DiasDeRiscoTabela dias={dashboard.dias_de_risco} />
      </section>
    </div>
  );
}

/**
 * Traduz o erro da consulta numa mensagem estável. 503 → indisponível; demais
 * `ErroHttp` reusam a mensagem do erro; não-`ErroHttp` (improvável; defesa) →
 * fallback genérico.
 */
function mensagemErro(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    if (erro.moduloIndisponivel) {
      return "Dashboard indisponível no momento.";
    }
    return erro.message;
  }
  return "Não foi possível carregar o dashboard.";
}
